import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react'
import type * as Y from 'yjs'
import { hrefAmmesso, ripulisciHtmlPosta, testoDaHtml, testoInHtml } from '@cidis/shared/html-posta'
import * as bozze from '../../servizi/bozze.ts'
import { Bottone, Campo, Modale } from '../../componenti/base.tsx'

/**
 * Il corpo del messaggio con la formattazione (decisione di Davide del 2/11/2026): grassetto, corsivo,
 * sottolineato, elenchi, citazione, collegamenti, togli formattazione. Scrive due testi condivisi della
 * bozza (505): `html`, il corpo ripulito (packages/shared/src/html-posta.ts), e `testo`, la sua versione
 * semplice, che resta quella su cui lavorano firma (508), invio e lettura della bozza. Le bozze scritte
 * prima hanno solo `testo`: l'editor parte da quello.
 *
 * L'incolla da Word, Outlook o dal web passa dalla stessa ripulitura: restano paragrafi, elenchi,
 * grassetti e collegamenti, spariscono stili, immagini e tabelle. L'API ripulisce di nuovo all'invio.
 * Le modifiche di chi scrive nella stessa bozza ridisegnano il corpo quando non lo si sta toccando.
 */

/** Sostituisce il contenuto di un testo condiviso con la modifica minima. */
function scriviTesto(t: Y.Text, nuovo: string, origine: unknown) {
  const vecchio = t.toString()
  const d = bozze.differenza(vecchio, nuovo, nuovo.length)
  if (!d) return
  t.doc!.transact(() => {
    if (d.togli) t.delete(d.da, d.togli)
    if (d.metti) t.insert(d.da, d.metti)
  }, origine)
}

/** Il corpo da mostrare: l'HTML della bozza, o il testo semplice delle bozze di prima. */
function corpoDa(doc: Y.Doc): string {
  const html = doc.getText('html').toString()
  return html || testoInHtml(doc.getText('testo').toString())
}

type Comando = { nome: string; etichetta: string; tasto?: string; esegui: () => void }

export function EditorMessaggio({ doc, disabilitato, suFuoco }: {
  doc: Y.Doc
  disabilitato: boolean
  /** Presenza nella bozza condivisa: chi scrive il corpo (505). */
  suFuoco?: () => void
}) {
  const id = useId()
  const area = useRef<HTMLDivElement>(null)
  const [collegamento, setCollegamento] = useState<{ indirizzo: string; errore: string | null } | null>(null)
  const selezione = useRef<Range | null>(null)

  // Primo disegno e modifiche che non vengono da qui: altri autori, la firma che cambia con «Da».
  useLayoutEffect(() => {
    const el = area.current
    if (!el) return
    el.innerHTML = corpoDa(doc)
    const html = doc.getText('html')
    const testo = doc.getText('testo')
    const ridisegna = (_: unknown, t: Y.Transaction) => {
      if (t.origin === bozze.ORIGINE_LOCALE) return
      const nuovo = corpoDa(doc)
      // Mentre si scrive non si ridisegna sotto il cursore: le modifiche altrui arrivano al primo stacco.
      if (document.activeElement === el && t.origin === bozze.ORIGINE_REMOTA) return
      if (ripulisciHtmlPosta(el.innerHTML) !== nuovo) el.innerHTML = nuovo
    }
    html.observe(ridisegna)
    testo.observe(ridisegna)
    return () => { html.unobserve(ridisegna); testo.unobserve(ridisegna) }
  }, [doc])

  /** Dopo ogni modifica locale: HTML ripulito e testo semplice nella bozza. */
  function salva() {
    const el = area.current
    if (!el) return
    const html = ripulisciHtmlPosta(el.innerHTML)
    scriviTesto(doc.getText('html'), html, bozze.ORIGINE_LOCALE)
    scriviTesto(doc.getText('testo'), testoDaHtml(html), bozze.ORIGINE_LOCALE)
  }

  function esegui(comando: string, valore?: string) {
    area.current?.focus()
    document.execCommand(comando, false, valore)
    salva()
  }

  const comandi: Comando[] = [
    { nome: 'bold', etichetta: 'Grassetto', tasto: 'b', esegui: () => esegui('bold') },
    { nome: 'italic', etichetta: 'Corsivo', tasto: 'i', esegui: () => esegui('italic') },
    { nome: 'underline', etichetta: 'Sottolineato', tasto: 'u', esegui: () => esegui('underline') },
    { nome: 'insertUnorderedList', etichetta: 'Elenco puntato', esegui: () => esegui('insertUnorderedList') },
    { nome: 'insertOrderedList', etichetta: 'Elenco numerato', esegui: () => esegui('insertOrderedList') },
    { nome: 'blockquote', etichetta: 'Citazione', esegui: () => esegui('formatBlock', 'blockquote') },
    { nome: 'removeFormat', etichetta: 'Togli formattazione', esegui: () => {
      esegui('removeFormat')
      esegui('unlink')
    } },
  ]

  function apriCollegamento() {
    const s = window.getSelection()
    selezione.current = s && s.rangeCount && area.current?.contains(s.anchorNode) ? s.getRangeAt(0).cloneRange() : null
    setCollegamento({ indirizzo: 'https://', errore: null })
  }

  function inserisciCollegamento() {
    if (!collegamento) return
    const grezzo = collegamento.indirizzo.trim()
    const indirizzo = /^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i.test(grezzo) ? `mailto:${grezzo}` : grezzo
    const href = hrefAmmesso(indirizzo)
    if (!href) {
      setCollegamento({ ...collegamento, errore: 'Scrivi un indirizzo che inizia con https:// oppure un indirizzo email.' })
      return
    }
    area.current?.focus()
    const s = window.getSelection()
    if (selezione.current && s) { s.removeAllRanges(); s.addRange(selezione.current) }
    // Senza testo selezionato il collegamento mostra l'indirizzo: chi legge vede dove porta.
    if (!s || s.isCollapsed) document.execCommand('insertText', false, href.replace(/^mailto:/i, ''))
    if (s && s.isCollapsed && s.anchorNode) {
      const r = document.createRange()
      const lunghezza = href.replace(/^mailto:/i, '').length
      r.setStart(s.anchorNode, Math.max(0, s.anchorOffset - lunghezza))
      r.setEnd(s.anchorNode, s.anchorOffset)
      s.removeAllRanges(); s.addRange(r)
    }
    document.execCommand('createLink', false, href)
    setCollegamento(null)
    salva()
  }

  // Scorciatoie: Ctrl o Cmd con B, I, U, K (collegamento). Le prime tre le fa anche il browser: qui si
  // passa da execCommand perche' dopo il comando la bozza si aggiorna.
  function tasti(e: KeyboardEvent<HTMLDivElement>) {
    if (!(e.metaKey || e.ctrlKey) || e.altKey) return
    const tasto = e.key.toLowerCase()
    const c = comandi.find((x) => x.tasto === tasto)
    if (c) { e.preventDefault(); c.esegui(); return }
    if (tasto === 'k') { e.preventDefault(); apriCollegamento() }
  }

  // Incolla: solo HTML ripulito o testo, mai stili, immagini o script di chi l'ha scritto.
  useEffect(() => {
    const el = area.current
    if (!el) return
    const incolla = (e: ClipboardEvent) => {
      const dati = e.clipboardData
      if (!dati) return
      e.preventDefault()
      const html = dati.getData('text/html')
      const pulito = html ? ripulisciHtmlPosta(html) : testoInHtml(dati.getData('text/plain'))
      document.execCommand('insertHTML', false, pulito)
      salva()
    }
    const rilascio = (e: DragEvent) => { e.preventDefault() }
    el.addEventListener('paste', incolla)
    el.addEventListener('drop', rilascio)
    return () => { el.removeEventListener('paste', incolla); el.removeEventListener('drop', rilascio) }
    // salva legge l'elemento e il documento correnti: il legame si fa una volta.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc])

  return (
    <div className="campo editor-messaggio">
      <label id={`${id}-etichetta`}>Messaggio</label>
      <div className="editor-barra" role="toolbar" aria-label="Formattazione" aria-controls={id}>
        {comandi.map((c) => (
          <button key={c.nome} type="button" className={`editor-comando editor-${c.nome}`} disabled={disabilitato}
                  title={c.tasto ? `${c.etichetta} (Ctrl+${c.tasto.toUpperCase()})` : c.etichetta}
                  aria-label={c.etichetta}
                  // Il pulsante non prende il fuoco: la selezione nel corpo resta dov'era.
                  onMouseDown={(e) => e.preventDefault()} onClick={c.esegui}>
            <span aria-hidden="true">{SIMBOLI[c.nome]}</span>
          </button>
        ))}
        <button type="button" className="editor-comando" disabled={disabilitato} title="Collegamento (Ctrl+K)"
                aria-label="Collegamento" onMouseDown={(e) => e.preventDefault()} onClick={apriCollegamento}>
          <span aria-hidden="true">🔗</span>
        </button>
      </div>
      <div id={id} ref={area} className="editor-corpo" role="textbox" aria-multiline="true"
           aria-labelledby={`${id}-etichetta`} contentEditable={!disabilitato} suppressContentEditableWarning
           spellCheck onInput={salva} onBlur={salva} onFocus={suFuoco} onKeyDown={tasti} />
      {collegamento && (
        <Modale titolo="Inserisci un collegamento" chiudi={() => setCollegamento(null)}
          azioni={
            <>
              <Bottone variante="discreto" onClick={() => setCollegamento(null)}>Annulla</Bottone>
              <Bottone variante="primario" onClick={inserisciCollegamento}>Inserisci</Bottone>
            </>
          }>
          <Campo etichetta="Indirizzo" valore={collegamento.indirizzo} errore={collegamento.errore ?? undefined}
                 aiuto="Un indirizzo che inizia con https://, oppure un indirizzo email. Chi riceve vede il testo del collegamento: se non selezioni niente, il testo è l’indirizzo."
                 cambia={(v) => setCollegamento({ indirizzo: v, errore: null })} />
          {hrefAmmesso(collegamento.indirizzo.trim()) && (
            <p className="piccolo">Porta a: <span className="collegamento-url">{collegamento.indirizzo.trim()}</span></p>
          )}
        </Modale>
      )}
    </div>
  )
}

const SIMBOLI: Record<string, string> = {
  bold: 'G', italic: 'C', underline: 'S', insertUnorderedList: '•', insertOrderedList: '1.',
  blockquote: '❝', removeFormat: '⌫',
}
